import type { JobWithRelations, JobCategoryWithTags } from "@/types/db";
import type { LocationsResponse } from "@/lib/locations";
import SearchFilter, { type SelectedFilter } from "./searchFilters";
import SearchFilterMobile from "./searchFiltersMobile";
import EmailCaptureForm from "@/components/common/emailCaptureForm";
import JobCard from "@/components/common/jobs/jobCard";
import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { joinTags } from "@/lib/tagFilterUrl";
import { trackSearch } from "@/hooks/useAnalytics";
import { useInfiniteJobs } from "@/hooks/useInfiniteJobs";
import { useIntersectionObserver } from "@/hooks/useIntersectionObserver";
import { useJobImpressions } from "@/hooks/useJobImpressions";

interface SearchPageProps {
  jobCategoriesWithTags: JobCategoryWithTags[];
  locations: LocationsResponse;
  initialJobs: JobWithRelations[];
  initialTotal: number;
  initialTagSlugs?: string[];
  initialSearch?: string;
  initialCountry?: string;
  initialState?: string;
  /**
   * Tag slugs implied by the current pathname (e.g. ["python-data-jobs"] on
   * /python-data-jobs, ["remote-data-jobs", "python-data-jobs"] on /remote/
   * python-data-jobs). SearchClient keeps these implicit in the path and only
   * writes additional filter slugs to `?tags=` so the URL stays canonical.
   * Removing any of them navigates the user to /search (broad recovery path).
   */
  pathTagSlugs?: string[];
}

const RemoveIcon = () => (
  <svg
    className="size-3"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);

// Pipeline runs daily at 11am Mountain Time (America/Denver — handles DST).
const RUN_HOUR_MT = 11;
const COUNTDOWN_THRESHOLD_MIN = 3 * 60;

function getFreshness(): {
  updatedToday: boolean;
  minutesUntilNextRun: number;
} {
  const now = new Date();
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Denver",
    hour: "numeric",
    minute: "numeric",
    hour12: false,
  }).formatToParts(now);
  const hourPart = parts.find((p) => p.type === "hour");
  const minutePart = parts.find((p) => p.type === "minute");
  if (!hourPart || !minutePart) {
    // Extremely unlikely with the options above, but don't crash the client.
    return { updatedToday: false, minutesUntilNextRun: 60 };
  }
  const hour = Number(hourPart.value);
  const minute = Number(minutePart.value);

  const minutesSinceTodayRun = (hour - RUN_HOUR_MT) * 60 + minute;
  const updatedToday = minutesSinceTodayRun >= 0;
  const minutesUntilNextRun = updatedToday
    ? 24 * 60 - minutesSinceTodayRun
    : -minutesSinceTodayRun;

  return { updatedToday, minutesUntilNextRun };
}

function DataFreshness() {
  const [state, setState] = useState<ReturnType<typeof getFreshness> | null>(
    null,
  );

  useEffect(() => {
    const tick = () => setState(getFreshness());
    tick();
    const interval = setInterval(tick, 60_000);
    return () => clearInterval(interval);
  }, []);

  if (!state) return null;

  const { updatedToday, minutesUntilNextRun } = state;
  const showCountdown = minutesUntilNextRun <= COUNTDOWN_THRESHOLD_MIN;

  let countdown: string | null = null;
  if (showCountdown) {
    const h = Math.floor(minutesUntilNextRun / 60);
    const m = minutesUntilNextRun % 60;
    countdown = h > 0 ? `${h}h ${m}m` : `${m}m`;
  }

  return (
    <>
      {" "}
      <span aria-hidden="true">·</span> Updated{" "}
      {updatedToday ? "today" : "yesterday"}
      {countdown && (
        <>
          {" "}
          <span aria-hidden="true">·</span> Fresh roles in{" "}
          <span className="tabular-nums">{countdown}</span>
        </>
      )}
    </>
  );
}

export default function SearchClient({
  jobCategoriesWithTags,
  locations,
  initialJobs,
  initialTotal,
  initialTagSlugs = [],
  initialSearch,
  initialCountry,
  initialState,
  pathTagSlugs = [],
}: SearchPageProps) {
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const pathTagSet = useMemo(() => new Set(pathTagSlugs), [pathTagSlugs]);
  const [searchQuery, setSearchQuery] = useState(initialSearch ?? "");
  const [debouncedSearch, setDebouncedSearch] = useState(initialSearch ?? "");

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const [selectedFilters, setSelectedFilters] = useState<SelectedFilter[]>(
    () => {
      if (initialTagSlugs.length === 0) return [];
      const filters: SelectedFilter[] = [];
      for (const slug of initialTagSlugs) {
        for (const category of jobCategoriesWithTags) {
          const tag = category.jobTags.find((t) => t.slug === slug);
          if (tag) {
            filters.push({
              categoryId: category.id,
              categoryName: category.name,
              tag,
            });
            break;
          }
        }
      }
      return filters;
    },
  );

  const tagSlugs = useMemo(
    () => selectedFilters.map((f) => f.tag.slug),
    [selectedFilters],
  );

  const isInitialRender = useRef(true);
  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }
    trackSearch(debouncedSearch, tagSlugs);
  }, [debouncedSearch, tagSlugs]);

  // Keep the URL in sync with the filter set so users can share, bookmark, and
  // back-button to specific filter combinations. On a tag page, path-implicit
  // slugs are stripped from the query so the canonical URL stays clean. If
  // the user removes a path-implicit tag, we navigate to /search (broad
  // recovery path) since /python with no python filter is misleading.
  //
  // Dep list is narrow (only filter/search state) so unrelated re-renders
  // don't push identical URL writes. Non-listed values are read through refs
  // so a stale closure never fires navigate() against a URL the user has
  // since moved away from (e.g. clearing the country chip would otherwise
  // get undone by a subsequent filter toggle).
  const syncCtxRef = useRef({
    pathTagSlugs,
    pathTagSet,
    initialCountry,
    initialState,
    pathname,
    navigate,
  });
  syncCtxRef.current = {
    pathTagSlugs,
    pathTagSet,
    initialCountry,
    initialState,
    pathname,
    navigate,
  };

  const didSyncOnce = useRef(false);
  useEffect(() => {
    if (!didSyncOnce.current) {
      didSyncOnce.current = true;
      return;
    }
    const ctx = syncCtxRef.current;

    const allSlugs = selectedFilters.map((f) => f.tag.slug);
    const pathTagRemoved =
      ctx.pathTagSlugs.length > 0 &&
      !ctx.pathTagSlugs.every((s) => allSlugs.includes(s));

    const extraSlugs = allSlugs.filter((s) => !ctx.pathTagSet.has(s));
    const searchObj: Record<string, unknown> = {};
    if (debouncedSearch) searchObj.q = debouncedSearch;
    const extraJoined = joinTags(extraSlugs);
    if (extraJoined) searchObj.tags = extraJoined;
    if (ctx.initialCountry) searchObj.country = ctx.initialCountry;
    if (ctx.initialState) searchObj.state = ctx.initialState;

    if (pathTagRemoved) {
      const fullSearch: Record<string, unknown> = { ...searchObj };
      const fullJoined = joinTags(allSlugs);
      if (fullJoined) fullSearch.tags = fullJoined;
      ctx.navigate({ to: "/search", search: fullSearch, replace: false });
      return;
    }

    ctx.navigate({ to: ctx.pathname, search: searchObj, replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, tagSlugs]);

  const { jobs, total, fetchNextPage, hasMore, isFetchingNextPage, isLoading } =
    useInfiniteJobs({
      initialJobs,
      initialTotal,
      tags: tagSlugs,
      search: debouncedSearch,
      country: initialCountry,
      state: initialState,
    });

  const { ref: sentinelRef, isIntersecting } = useIntersectionObserver();
  const { observeRef } = useJobImpressions();

  useEffect(() => {
    if (isIntersecting && hasMore && !isFetchingNextPage) {
      const timer = setTimeout(() => fetchNextPage(), 300);
      return () => clearTimeout(timer);
    }
  }, [isIntersecting, hasMore, isFetchingNextPage, fetchNextPage]);

  const handleSelectFilter = (filter: SelectedFilter) => {
    setSelectedFilters((prev) => {
      const existingIndex = prev.findIndex(
        (f) => f.categoryId === filter.categoryId && f.tag.id === filter.tag.id,
      );
      if (existingIndex !== -1) {
        return prev.filter((_, i) => i !== existingIndex);
      }
      return [
        ...prev.filter((f) => f.categoryId !== filter.categoryId),
        filter,
      ];
    });
  };

  const handleRemoveFilter = (categoryId: number) => {
    setSelectedFilters((prev) =>
      prev.filter((f) => f.categoryId !== categoryId),
    );
  };

  const handleClearAllFilters = () => {
    setSelectedFilters([]);
    // On any page that carries implicit filter context (tag pages, /remote,
    // country/state pages), "Clear all" leaves the user on a URL whose path
    // still implies filters. Navigate to /search for a clean slate.
    if (pathTagSlugs.length > 0 || initialCountry || initialState) {
      navigate({ to: "/search" });
    }
  };

  const locationLabel = (value: string): string => {
    const lower = value.toLowerCase();
    for (const country of locations.countries) {
      if (country.value.toLowerCase() === lower) return country.name;
      for (const state of country.states) {
        if (state.value.toLowerCase() === lower) return state.name;
      }
    }
    return value;
  };

  const hasAnyFilter =
    selectedFilters.length > 0 || !!initialCountry || !!initialState;

  return (
    <>
      <main className="relative mx-auto mt-8 mb-20 flex w-full max-w-7xl flex-col px-4 sm:px-6 lg:flex-row lg:gap-x-8 lg:px-8">
        <aside
          id="filterContainer"
          className="hidden lg:block lg:w-80 lg:shrink-0"
        >
          <SearchFilter
            categoriesWithTags={jobCategoriesWithTags}
            locations={locations}
            selectedFilters={selectedFilters}
            onSelectFilter={handleSelectFilter}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            activeCountry={initialCountry}
            activeState={initialState}
          />
          <div className="sticky top-4 mt-4">
            <EmailCaptureForm variant="sidebar" />
          </div>
        </aside>

        <SearchFilterMobile
          categoriesWithTags={jobCategoriesWithTags}
          locations={locations}
          selectedFilters={selectedFilters}
          onSelectFilter={handleSelectFilter}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          activeCountry={initialCountry}
          activeState={initialState}
        />

        <section id="jobList" className="min-w-0 flex-1">
          <div className="jobListContainer flex min-h-[50vh] w-full flex-col gap-2 text-left">
            {hasAnyFilter && (
              <div className="mb-4 flex w-full flex-wrap items-center gap-2">
                {selectedFilters.map((filter) => (
                  <span
                    key={filter.categoryId}
                    className="inline-flex items-center gap-1.5 rounded-full bg-orange-100 py-1 pr-2 pl-3 text-sm text-orange-800"
                  >
                    <span className="font-semibold">
                      {filter.categoryName}:
                    </span>
                    <span>{filter.tag.name}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveFilter(filter.categoryId)}
                      aria-label={`Remove ${filter.categoryName} filter: ${filter.tag.name}`}
                      className="ml-0.5 rounded-full p-0.5 text-orange-700 transition-colors hover:bg-orange-200 hover:text-orange-900 focus-visible:ring-2 focus-visible:ring-orange-400 focus-visible:outline-none"
                    >
                      <RemoveIcon />
                    </button>
                  </span>
                ))}
                {initialCountry && (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-orange-100 py-1 pr-2 pl-3 text-sm text-orange-800">
                    <span className="font-semibold">Country:</span>
                    <span>{locationLabel(initialCountry)}</span>
                    <button
                      type="button"
                      onClick={() =>
                        navigate({
                          to: "/search",
                          search: { state: initialState },
                        })
                      }
                      aria-label={`Remove country filter: ${locationLabel(initialCountry)}`}
                      className="ml-0.5 rounded-full p-0.5 text-orange-700 transition-colors hover:bg-orange-200 hover:text-orange-900 focus-visible:ring-2 focus-visible:ring-orange-400 focus-visible:outline-none"
                    >
                      <RemoveIcon />
                    </button>
                  </span>
                )}
                {initialState && (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-orange-100 py-1 pr-2 pl-3 text-sm text-orange-800">
                    <span className="font-semibold">State:</span>
                    <span>{locationLabel(initialState)}</span>
                    <button
                      type="button"
                      onClick={() =>
                        navigate({
                          to: "/search",
                          search: { country: initialCountry },
                        })
                      }
                      aria-label={`Remove state filter: ${locationLabel(initialState)}`}
                      className="ml-0.5 rounded-full p-0.5 text-orange-700 transition-colors hover:bg-orange-200 hover:text-orange-900 focus-visible:ring-2 focus-visible:ring-orange-400 focus-visible:outline-none"
                    >
                      <RemoveIcon />
                    </button>
                  </span>
                )}
                <button
                  type="button"
                  onClick={handleClearAllFilters}
                  className="text-sm text-gray-500 transition-colors hover:text-gray-700 hover:underline focus-visible:ring-2 focus-visible:ring-orange-300 focus-visible:outline-none"
                >
                  Clear all
                </button>
              </div>
            )}

            <div className="mb-1 flex items-center">
              <p className="text-sm text-gray-500">
                <span className="font-semibold text-gray-700 tabular-nums">
                  {total.toLocaleString()}
                </span>{" "}
                open {total === 1 ? "job" : "jobs"}
                <DataFreshness />
              </p>
            </div>

            <div className="flex flex-col gap-3" id="jobListContainerUl">
              {jobs.map((job: JobWithRelations) => (
                <JobCard
                  key={job.id}
                  job={job}
                  variant="compact"
                  ref={observeRef}
                />
              ))}
            </div>

            <div ref={sentinelRef} className="h-4" />

            {isFetchingNextPage && (
              <div className="flex items-center justify-center gap-2 py-6">
                <div className="border-polaris-orange size-4 animate-spin rounded-full border-2 border-t-transparent" />
                <span className="text-sm text-gray-500">
                  Loading more jobs...
                </span>
              </div>
            )}

            {!isLoading && jobs.length === 0 && (
              <div className="flex flex-col items-center justify-center gap-4 py-16 text-center">
                <div>
                  <p className="text-lg font-semibold text-gray-700">
                    No jobs found
                  </p>
                  <p className="mt-1 text-sm text-gray-500">
                    Try adjusting your filters or search terms.
                  </p>
                </div>
                {hasAnyFilter && (
                  <button
                    type="button"
                    onClick={handleClearAllFilters}
                    className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm transition-colors hover:border-gray-400 hover:text-gray-900 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-500"
                  >
                    Clear all filters
                  </button>
                )}
              </div>
            )}
          </div>
        </section>
      </main>
      {/* Post-list capture rendered as a sibling of <main> so its orange band
        can span edge-to-edge at any viewport width. Inside <main> it would
        become a third flex column on lg+ (next to sidebar + jobList).
        Suppressed when the filter set returned zero results — the empty-
        state block handles that case without promoting the newsletter. */}
      {jobs.length > 0 && (
        <div className="-mb-20">
          <EmailCaptureForm
            variant="inline"
            cta="Don't want to keep searching? Get carefully selected data jobs, twice a week, straight to your inbox."
          />
        </div>
      )}
    </>
  );
}
